import { CellTable } from "@/atoms"
import { ICONS_INSIGHTS } from "@/const/insights-constants"
import { getDefaultColumnParams } from "@/helpers/table.helpers"
import { MoreTableCell } from "@/molecules/MoreTableCell"
import { AgentInsightsCell } from "@/pages/AiDiscovery/components/molecules/AgentInsightsCell/AgentInsightsCell"
import { AgentNameCell } from "@/pages/AiDiscovery/components/molecules/AgentNameCell"
import { DataDirectionCell } from "@/pages/AiDiscovery/components/molecules/DataDirectionCell"
import { SourceCell } from "@/pages/AiDiscovery/components/molecules/SourceCell"
import { ToolInsightsCell } from "@/pages/AiDiscovery/components/molecules/ToolInsightsCell"
import { WorkflowTypeCell } from "@/pages/AiDiscovery/components/molecules/WorkflowTypeCell"
import { EndpointCell, ValueCell } from "./McpServerCells"

import styles from "./AiComponentsTabAgentAiDiscoveryDrawer.module.scss"

import type { WorkflowTypeAi } from "@/api/useInventory.types"
import type { RowClick } from "@/hooks/useLinkDrawerTooltip"
import type { TFunction } from "@/hooks/useTranslation"
import type { McpGraphState } from "./AiComponentsTabAgentAiDiscoveryDrawer.types"

export const TABS = {
  agents: "agent",
  tools: "tool",
  models: "model",
  mcpServers: "mcp_server",
  knowledgeSources: "knowledge_source",
  // A workflow's nested workflows, and its steps, as the components tab lists them
  workflows: "workflow",
  modelCalls: "model_call",
  codeSteps: "code_step"
}

// The MCP clients segment lists the agent's clients, under this components path
export const MCP_CLIENTS_COMPONENT = "mcp_client"

// Clicking a client shows it in the graph; clicking it again clears the highlight
export const toggleHighlightedClient = (
  state: McpGraphState,
  clientId: string
): McpGraphState =>
  state.highlightedClientId === clientId
    ? { show: state.show }
    : { show: true, highlightedClientId: clientId }

// Clicking a tool or a knowledge source shows it in the graph; clicking it again clears the highlight
export const toggleHighlightedComponent = (
  state: McpGraphState,
  componentId: string
): McpGraphState =>
  state.highlightedComponentId === componentId
    ? { show: state.show }
    : { show: state.show, highlightedComponentId: componentId }

export const getTabColumns = (
  tab: string,
  t: TFunction,
  options: {
    showRequiredAccess: boolean
    showConnector?: boolean
    // Opens the drawer of a resource the source's popover lists
    onRowClick?: RowClick
  } = {
    showRequiredAccess: false
  }
) => {
  switch (tab) {
    case TABS.tools:
      return [
        {
          title: "Tool",
          render: CellTable,
          ...getDefaultColumnParams("name")
        },
        {
          title: "Categories",
          render: MoreTableCell,
          ...getDefaultColumnParams("dataCategoriesShowMore")
        },
        {
          title: "Data Direction",
          render: DataDirectionCell,
          ...getDefaultColumnParams("dataDirection")
        },
        {
          title: "Type",
          render: CellTable,
          ...getDefaultColumnParams("type")
        },
        ...(options.showConnector
          ? [
              {
                title: t("inventory.ai.tools.columns.connector"),
                render: CellTable,
                ...getDefaultColumnParams("connector")
              }
            ]
          : []),
        {
          title: "Insights",
          width: options.showRequiredAccess ? 130 : 80,
          render: ToolInsightsCell(t, options),
          ...getDefaultColumnParams("insights")
        }
      ]

    case TABS.models:
      return [
        {
          title: "Model",
          render: CellTable,
          ...getDefaultColumnParams("name")
        },
        {
          title: "Vendor",
          render: CellTable,
          ...getDefaultColumnParams("vendor")
        },
        {
          title: "Platform",
          render: CellTable,
          ...getDefaultColumnParams("platform")
        },
        {
          title: "Insights",
          width: 80,
          render: AgentInsightsCell(t),
          ...getDefaultColumnParams("insights")
        }
      ]
    case TABS.workflows:
      return [
        {
          title: "Workflow",
          render: AgentNameCell,
          width: 280,
          ...getDefaultColumnParams("name")
        },
        {
          title: "Type",
          render: (type: WorkflowTypeAi | null | undefined) =>
            WorkflowTypeCell({ type, t }),
          ...getDefaultColumnParams("type")
        },
        {
          title: "Insights",
          width: 120,
          render: AgentInsightsCell(t),
          ...getDefaultColumnParams("insights")
        }
      ]
    case TABS.agents:
      return [
        {
          title: "Agent",
          render: CellTable,
          ...getDefaultColumnParams("name")
        },
        {
          title: "Model",
          render: MoreTableCell,
          ...getDefaultColumnParams("modelShowMore")
        },
        {
          title: "Insights",
          render: AgentInsightsCell(t),
          width: 100,
          ...getDefaultColumnParams("insights")
        }
      ]
    case TABS.modelCalls:
      return [
        {
          title: "Model call",
          render: CellTable,
          ...getDefaultColumnParams("name")
        },
        {
          title: "Model",
          render: MoreTableCell,
          ...getDefaultColumnParams("modelShowMore")
        },
        {
          title: "Insights",
          width: 80,
          render: AgentInsightsCell(t, [ICONS_INSIGHTS.runtime]),
          ...getDefaultColumnParams("insights")
        }
      ]
    case TABS.codeSteps:
      return [
        {
          title: "Code step",
          render: CellTable,
          ...getDefaultColumnParams("name")
        },
        {
          title: "Categories",
          render: MoreTableCell,
          ...getDefaultColumnParams("dataCategoriesShowMore")
        },
        {
          title: "Data Direction",
          render: DataDirectionCell,
          ...getDefaultColumnParams("dataDirection")
        },
        {
          title: "Insights",
          width: 100,
          render: AgentInsightsCell(t, [
            ICONS_INSIGHTS.runtime,
            ICONS_INSIGHTS.hasGuardrails
          ]),
          ...getDefaultColumnParams("insights")
        }
      ]
    case TABS.mcpServers: {
      const sourceCell = SourceCell({
        t,
        onRowClick: options.onRowClick ?? (() => {})
      })

      return [
        {
          title: t("inventory.ai.mcpServers.columns.mcpClient"),
          render: CellTable,
          ...getDefaultColumnParams("name")
        },
        {
          title: t("inventory.ai.mcpServers.columns.locality"),
          render: ValueCell,
          ...getDefaultColumnParams("localityLabel")
        },
        {
          title: t("inventory.ai.mcpServers.columns.endpoint"),
          render: EndpointCell,
          ...getDefaultColumnParams("endpointUrl"),
          ellipsis: false
        },
        {
          title: t("inventory.ai.mcpServers.columns.relatedTools"),
          render: MoreTableCell,
          ...getDefaultColumnParams("relatedToolsShowMore")
        },
        {
          title: t("inventory.ai.mcpServers.columns.source"),
          render: sourceCell,
          className: styles.source,
          ...getDefaultColumnParams("projectName")
        }
      ]
    }
    case TABS.knowledgeSources:
      return [
        {
          title: t("inventory.ai.knowledgeSources.columns.name"),
          render: CellTable,
          ...getDefaultColumnParams("name")
        },
        {
          title: t("inventory.ai.knowledgeSources.columns.kind"),
          render: CellTable,
          ...getDefaultColumnParams("kindLabel")
        },
        {
          title: t("inventory.ai.knowledgeSources.columns.provider"),
          render: CellTable,
          ...getDefaultColumnParams("provider")
        },
        {
          title: t("inventory.ai.knowledgeSources.columns.location"),
          render: CellTable,
          ...getDefaultColumnParams("location")
        },
        {
          title: t("inventory.ai.knowledgeSources.columns.insights"),
          width: 80,
          render: AgentInsightsCell(t),
          ...getDefaultColumnParams("insights")
        }
      ]
    default:
      return []
  }
}
